import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = ['loader', 'iframe']
  static values = {
    autoResize: Boolean,
    passThroughParams: Boolean,
    theme: String,
    allowedUrlPattern: String,
  }

  connect() {
    this.setPassThroughParams()
  }

  onLoad() {
    this.loaderTarget.classList.add('!hidden')

    this.element.dispatchEvent(
      new CustomEvent('iframe:onLoad', {
        detail: { iframe: this.iframeTarget },
        bubbles: true,
      })
    )

    // Send theme to iframe after it loads
    this.sendThemeToIframe()

    this.setupNavigationRestriction()
  }

  resize(height) {
    if (!this.autoResizeValue) return

    // Resize iframe container to be as tall as the iframe contents
    this.iframeTarget.style.height = height + 'px'
  }

  setPassThroughParams() {
    if (!this.passThroughParamsValue) return

    const params = new URLSearchParams(window.location.search)
    const iframeSrc = new URL(this.iframeTarget.src)

    params.forEach((value, key) => iframeSrc.searchParams.append(key, value))

    this.iframeTarget.src = iframeSrc.toString()
  }

  // Iframes will send a message with their actual height on load and on resize
  handleMessage(message) {
    // Make sure to only handle messages from our iframe
    if (this.iframeTarget.contentWindow != message.source) return

    if (message.data.purpose == 'simplero_resize') {
      this.resize(message.data.height)
    } else if (message.data.purpose === 'request_theme') {
      this.sendThemeToIframe()
    }
  }

  isValidBackgroundColor(backgroundColor) {
    return (
      backgroundColor &&
      backgroundColor !== 'transparent' &&
      backgroundColor !== 'rgba(0, 0, 0, 0)' &&
      backgroundColor !== 'initial' &&
      backgroundColor !== 'inherit'
    )
  }

  getEffectiveBackgroundColor(element) {
    let currentElement = element

    // Look for .builder-node ancestors and check their .node-bg children
    // We apply background color on .node-bg so we can't just iterate the parents to figure this out
    while (currentElement && currentElement !== document.documentElement) {
      if (currentElement.classList?.contains('builder-node')) {
        const nodeBg = currentElement.querySelector('.node-bg')
        if (nodeBg) {
          const computedStyle = window.getComputedStyle(nodeBg)
          const backgroundColor = computedStyle.backgroundColor

          if (this.isValidBackgroundColor(backgroundColor)) {
            return backgroundColor
          }
        }
      }

      // Also check the current element's own background as fallback
      const computedStyle = window.getComputedStyle(currentElement)
      const backgroundColor = computedStyle.backgroundColor

      if (this.isValidBackgroundColor(backgroundColor)) {
        return backgroundColor
      }

      currentElement = currentElement.parentElement
    }

    // If no background found, check document.body and html
    const bodyBg = window.getComputedStyle(document.body).backgroundColor
    if (this.isValidBackgroundColor(bodyBg)) {
      return bodyBg
    }

    const htmlBg = window.getComputedStyle(
      document.documentElement
    ).backgroundColor
    if (this.isValidBackgroundColor(htmlBg)) {
      return htmlBg
    }

    // Default to white if nothing found
    return 'rgb(255, 255, 255)'
  }

  sendThemeToIframe() {
    if (!this.iframeTarget?.contentWindow) return

    let theme = 'light'

    if (this.themeValue === 'light') {
      theme = 'light'
    } else if (this.themeValue === 'dark') {
      theme = 'dark'
    } else if (this.themeValue === 'current') {
      theme = this.element.closest('.dark') ? 'dark' : 'light'
    } else {
      // Default to 'auto' behavior - detect from background color
      const effectiveBg = this.getEffectiveBackgroundColor(this.element)
      const rgb = effectiveBg.match(/\d+/g)

      if (rgb) {
        const [r, g, b] = rgb.map(Number)
        const brightness = (r * 299 + g * 587 + b * 114) / 1000
        theme = brightness < 128 ? 'dark' : 'light'
      }
    }

    this.iframeTarget.contentWindow.postMessage(
      // syncBackground: only `current` means we're actively mirroring our own live theme into
      // the child (e.g. the admin kickoff-call modal) — the child should paint an opaque themed
      // background. `auto`/`light`/`dark` compute a one-off theme for the child's *text*, but the
      // child is embedded on a page whose own background may differ (a builder element on a
      // custom-background section, the raw "Embed code" widget) — it must stay visually
      // transparent so that background shows through. See embedded_iframe_controller.js#applyTheme.
      { type: 'setTheme', theme, syncBackground: this.themeValue === 'current' },
      '*'
    )
  }

  setupNavigationRestriction() {
    if (!this.allowedUrlPatternValue) return

    try {
      const iframeDoc =
        this.iframeTarget.contentDocument ||
        this.iframeTarget.contentWindow.document

      if (!iframeDoc) return

      this.allowedUrlRegex = new RegExp(this.allowedUrlPatternValue)

      iframeDoc.addEventListener('click', this.handleLinkClick, true)
    } catch (e) {
      console.debug(
        'Cannot restrict iframe navigation: cross-origin iframe detected',
        e
      )
    }
  }

  handleLinkClick = (event) => {
    const target = event.target.closest('a')
    if (!target || !target.href) return

    const href = target.href

    if (!/^(https?|mailto|tel):/i.test(href)) return

    if (
      !this.allowedUrlRegex.test(href) &&
      !href.startsWith('#') &&
      !href.startsWith('javascript:')
    ) {
      event.preventDefault()

      if (
        !target.hasAttribute('data-method') &&
        !target.hasAttribute('data-remote')
      ) {
        window.open(href, '_blank', 'noopener,noreferrer')
      }
    }
  }
}
